/**
 * Gate 4 — Freshness
 *
 * Rejects stale-context decisions on time-sensitive topics.
 * When a freshness window is configured, every source timestamp
 * in the envelope must fall within that window of the current
 * evaluation time. Any timestamp outside the window trips the gate.
 *
 * Trip routes to abstention with reason='stale_context'.
 */

/**
 * @param {import('../disciplineEnforcer.js').EnforcerEnvelope} envelope
 * @param {object} [config]
 * @param {number} [config.nowMs] - override for testability (default: Date.now())
 * @returns {import('../disciplineEnforcer.js').GateResult}
 */
export function checkFreshness(envelope, config = {}) {
  const freshnessWindowMs = envelope.context?.freshnessWindowMs;
  const sourceTimestamps = envelope.context?.sourceTimestamps;

  if (!freshnessWindowMs || !Array.isArray(sourceTimestamps) || sourceTimestamps.length === 0) {
    return { gateName: 'freshness', verdict: 'pass', reason: null, payload: { skipped: true } };
  }

  const nowMs = config.nowMs ?? Date.now();
  const cutoffMs = nowMs - freshnessWindowMs;

  const staleTimestamps = sourceTimestamps.filter(ts => ts < cutoffMs);

  if (staleTimestamps.length > 0) {
    return {
      gateName: 'freshness',
      verdict: 'trip',
      reason: 'stale_context',
      payload: {
        freshnessWindowMs,
        staleTimestamps,
        oldestAllowedMs: cutoffMs,
        nowMs,
      },
    };
  }

  return {
    gateName: 'freshness',
    verdict: 'pass',
    reason: null,
    payload: { freshnessWindowMs, sourceTimestamps, allFresh: true },
  };
}
